import clsx from "clsx";
import { Color, hsl } from "d3-color";
import { useId, type CSSProperties } from "react";

interface LegendProps {
  series: { id: string; color: Color | string; display_name?: string }[];
  hiddenSeries: string[];
  toggleSeriesById: (id: string) => void;
  printable?: boolean;
  className?: string;
  direction?: "row" | "column";
}

export function Legend({
  hiddenSeries,
  series,
  toggleSeriesById,
  printable,
  className,
  direction = "column",
}: LegendProps) {
  const legendId = useId();

  return (
    <div className={clsx("flex gap-2 pb-2", direction === "column" ? "flex-col" : "flex-row", className)}>
      {series.map(({ id, color, display_name }) => {
        const labelId = `series-${id}-${legendId}`;

        const defaultStyles: CSSProperties = {
          accentColor: toAccentColour(color).toString(),
        };

        const styles: CSSProperties = printable
          ? {
              ...defaultStyles,
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
              backgroundColor: rgbToHex(color.toString()),
              width: "16px",
              height: "16px",
              display: "inline-block",
              verticalAlign: "middle",
            }
          : defaultStyles;

        return (
          <label key={id} htmlFor={labelId} className="flex! gap-x-2 items-center cursor-pointer">
            <input
              id={labelId}
              type="checkbox"
              checked={!hiddenSeries.includes(id)}
              onChange={() => toggleSeriesById(id)}
              style={styles}
              className="print:checked:appearance-none"
            />

            <div className="text-xs text-neutral-600 truncate">{display_name}</div>
          </label>
        );
      })}
    </div>
  );
}
function toAccentColour(color: Color | string): Color {
  if (typeof color === "string") {
    return hsl(color).darker(0.3);
  }
  return hsl(color.formatHsl()).darker(0.3);
}
function rgbToHex(rgbString: string): string {
  const rgb = rgbString.replace("rgb(", "").replace(")", "").split(",").map(Number);
  const hex = rgb.map((value) => value.toString(16).padStart(2, "0")).join("");
  return `#${hex}`;
}
